import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPastOrderDetailsAsync } from "../../redux/features/order/orderThunks";
import {
  selectPastOrderDetails,
} from "../../redux/features/order/orderSelectors";
import {
  selectCartItems,
  selectCartRestaurantDetails,
} from "../../redux/features/restaurants/restaurantSelectors";
import { getBranchDetailsAsync, getPickupBranchDetails } from "../../redux/features/restaurants/restaurantThunks";
import { clearCart, setProductDetail, setShowProductDetail } from "../../redux/features/restaurants/restaurantSlice";
import WarningModal from "../modal";
import { selectSelectedAddress, selectServiceType } from "../../redux/features/customer/customerSelectors";

const OrderCard = ({
  restaurant,
  branchName,
  date,
  items,
  total,
  orderStatus,
  notes,
  orderId,
  selectedBranchArea,
  delivery_charges,
  order_no,
}) => {
  const isReorderDisabled = [
    "Ready for Dispatch",
    "Preparing",
    "On the way",
    "Submitted",
    "Accepted",
  ].includes(orderStatus);
  const [showWarningModal, setWarningModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pastOrderDetails = useSelector(selectPastOrderDetails);
  const cartItems = useSelector(selectCartItems);
  const existingRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const selectedAddress = useSelector(selectSelectedAddress);
  const serviceType = useSelector(selectServiceType);

  // Define button styles based on the status
  const getButtonStyle = (status) => {
    switch (status) {
      case "Submitted":
        return "bg-[#F7B500] text-white";
      case "Accepted":
        return "bg-[#02BBD5] text-white";
      case "Canceled":
        return "bg-[#E02020] text-white";
      case "Ready for Dispatch":
        return "bg-[#F7B500] text-white";
      case "Preparing":
        return "bg-[#0091FF] text-white";
      case "On the way":
        return "bg-[#6236FF] text-white";
      case "Delivered":
        return "bg-[#4E948A] text-white";
      default:
        return "bg-gray-400 text-black";
    }
  };

  const handleCancel = () => {
    setWarningModal(false);
    setIsLoadingOrderDetails(false);
  };

  const handleErrorClose = () => {
    setShowErrorModal(false);
    setIsLoadingOrderDetails(false);
  };

  const handleProceed = async () => {
    setWarningModal(false);
    if (pastOrderDetails && Object.keys(pastOrderDetails).length > 0) {
      dispatch(clearCart());
      dispatch(setProductDetail(null));
      dispatch(setShowProductDetail(false));

      const restaurantId = pastOrderDetails.restaurant_id;
      const areaName = selectedAddress?.area;
      const restaurantName = pastOrderDetails.restaurant_name_en || restaurant;
      const branchId = pastOrderDetails.branch_id;

      try {
        let result;
        if (serviceType === "delivery") {
          // Fetch branch details for delivery
          result = await dispatch(
            getBranchDetailsAsync(restaurantId, areaName, restaurantName)
          );
        } else {
          // Get branch details for pickup (synchronous)
          result = dispatch(
            getPickupBranchDetails(branchId)
          );
        }
        
        // If the request was not successful
        if (!result.success) {
          setShowErrorModal(true);
          return;
        }
        
        // If successful, navigate to place order
        navigate("/place-order");
      } catch (error) {
        console.error("Error fetching branch details:", error);
        setShowErrorModal(true);
      }
    } else {
      // Show error modal if pastOrderDetails is empty
      setShowErrorModal(true);
    }
  };

  // Monitor pastOrderDetails changes when loading
  useEffect(() => {
    // Check if loading state is active
    if (isLoadingOrderDetails) {
      // Case 1: We have valid order details
      if (pastOrderDetails && Object.keys(pastOrderDetails).length > 0) {
        setIsLoadingOrderDetails(false);

        // Check if we need to show warning modal (if cart has items and restaurant is different)
        if (
          cartItems.length !== 0 &&
          existingRestaurantDetails &&
          existingRestaurantDetails.restaurantId !==
            pastOrderDetails.restaurant_id
        ) {
          setWarningModal(true);
        } else {
          // If no conflict, directly proceed with reorder
          handleProceed();
        }
      }
      // Case 2: We don't have valid order details (empty or null response)
      else {
        // Show error modal when no past orders exist
        setShowErrorModal(true);
      }
    }
  }, [pastOrderDetails]);

  const onReorderClick = async () => {
    try {
      setIsLoadingOrderDetails(true);
      // Fetch the past order details
      await dispatch(getPastOrderDetailsAsync(orderId));

      // The useEffect above will handle the rest of the process when pastOrderDetails updates
    } catch (error) {
      console.error("Error fetching order details:", error);
      setIsLoadingOrderDetails(false);
      setShowErrorModal(true);
    }
  };

  return (
    <div className="border-2 border-slate-300 rounded-lg p-3 shadow-lg w-full h-auto bg-neutral-50">
      {/* Header with improved wrapping */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-lg text-gray-500 font-semibold truncate max-w-[calc(100%-80px)]">{restaurant}</h2>
        <span className="text-xs text-gray-500 whitespace-nowrap">{date}</span>
      </div>
      <div className="text-xs text-gray-600 truncate">{branchName}</div>
      <div className="text-xs text-gray-600 truncate">Order #: {order_no}</div>

      {/* Items with improved overflow handling */}
      <ul className="mt-2 w-full">
        {items.map((item, index) => (
          <li key={index} className="w-full">
            {/* Main item */}
            <div className="flex justify-between w-full overflow-hidden">
              <span className="text-sm text-gray-500 truncate max-w-[70%]">
                {item.quantity}x {item.name.split(' (')[0]}
              </span>
              <span className="text-gray-500 text-xs whitespace-nowrap ml-1">
                {item.price}
              </span>
            </div>
            
            {/* Variants as separate indented lines */}
            {item.variants && item.variants.length > 0 && (
              <ul className="pl-4 w-full">
                {item.variants.map((variant, vIndex) => (
                  <li key={vIndex} className="flex justify-between w-full overflow-hidden">
                    <span className="text-xs text-gray-400 max-w-[70%]">
                      {variant.quantity}x {variant.name}
                    </span>
                    <span className="text-gray-400 text-xs whitespace-nowrap ml-1">
                      {variant.price}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {/* Total with constrained width */}
      <hr style={{ marginTop: "8px", borderColor: "#959FA8" }} />
      <div className="flex justify-between mt-1 w-full">
        <span className="text-sm font-semibold text-gray-500">Subtotal</span>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          KWD {items?.reduce((total, item) => {
            let itemTotal = parseFloat(item.price.replace('KWD ', ''));
            if (item.variants && item.variants.length > 0) {
              itemTotal += item.variants?.reduce((variantTotal, variant) => {
                return variantTotal + parseFloat(variant.price.replace('KWD ', ''));
              }, 0);
            }
            return total + itemTotal;
          }, 0).toFixed(3)}
        </span>
      </div>
      {delivery_charges > 0 && (
        <div className="flex justify-between mt-1 w-full">
          <span className="text-sm font-semibold text-gray-500">Delivery Charges</span>
          <span className="text-xs text-gray-500 whitespace-nowrap">KWD {delivery_charges.toFixed(3)}</span>
        </div>
      )}
      <div className="flex justify-between mt-1 w-full">
        <span className="text-sm font-semibold text-gray-500">Total</span>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          KWD {(items?.reduce((total, item) => {
            let itemTotal = parseFloat(item.price.replace('KWD ', ''));
            if (item.variants && item.variants.length > 0) {
              itemTotal += item.variants?.reduce((variantTotal, variant) => {
                return variantTotal + parseFloat(variant.price.replace('KWD ', ''));
              }, 0);
            }
            return total + itemTotal;
          }, 0) + (delivery_charges || 0)).toFixed(3)}
        </span>
      </div>
      <hr style={{ marginTop: "5px", borderColor: "#959FA8" }} />
      
      {/* Notes with ellipsis for overflow */}
      <div className="block text-sm font-medium text-gray-500 mt-1 mb-3 truncate">
        Notes: {notes ? notes : "N/A"}
      </div>

      {/* Responsive Action Buttons - Fix overflowing buttons */}
      <div className="flex flex-col sm:flex-row w-full mt-2">
        <div className="flex justify-between w-full gap-2 flex-wrap">
          {/* Status Button */}
          <button
            className={`${getButtonStyle(
              orderStatus
            )} text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center mb-1 sm:mb-0`}
          >
            {orderStatus}
          </button>

          {/* Re-Order Button */}
          <button
            className={`text-xs font-bold rounded-md px-2 h-[21px] flex-shrink-0 text-center ${
              isReorderDisabled || isLoadingOrderDetails
                ? "bg-amber-50 text-white cursor-not-allowed"
                : "bg-amber-100 text-white hover:bg-amber-200"
            }`}
            disabled={isReorderDisabled || isLoadingOrderDetails}
            onClick={
              !isReorderDisabled && !isLoadingOrderDetails
                ? onReorderClick
                : undefined
            }
          >
            {isLoadingOrderDetails ? "Loading..." : "Re-Order"}
          </button>
        </div>
      </div>

      {/* Warning Modal */}
      {showWarningModal && (
        <WarningModal
          onCancel={handleCancel}
          onProceed={handleProceed}
          description={`You're about to change restaurant from ${
            existingRestaurantDetails?.restaurantName
          } to ${pastOrderDetails?.restaurant_name_en || restaurant}`}
          subDescription="Current cart will be emptied. Do you wish to proceed?"
          confirmButtonText="Confirm"
          cancelButtonText="Cancel"
          showItems={false}
        />
      )}

      {/* Error Modal */}
      {showErrorModal && serviceType === "delivery" && (
        <WarningModal
          onCancel={handleErrorClose}
          onProceed={handleErrorClose}
          description="Delivery not available"
          subDescription="This restaurant does not deliver to the selected address, please select a different address to proceed with Reorder."
          confirmButtonText="OK"
          showCancelButton={false}
          showItems={false}
        />
      )}
    </div>
  );
};

// PropTypes validation
OrderCard.propTypes = {
  restaurant: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
    })
  ).isRequired,
  total: PropTypes.string.isRequired,
  isFirstCard: PropTypes.bool,
  orderStatus: PropTypes.string.isRequired,
  notes: PropTypes.string,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  delivery_charges: PropTypes.number,
  order_no: PropTypes.string,
};

OrderCard.defaultProps = {
  isFirstCard: false,
  notes: "",
  delivery_charges: 0,
  order_no: "",
};

export default OrderCard;
